import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../context/AuthContext';
import { useLanguage } from '../context/LanguageContext';
import { userService } from '../services/dataService';
import { apiErrorMessage } from '../context/ToastContext';
import { Card } from 'primereact/card';
import { Button } from 'primereact/button';
import { InputText } from 'primereact/inputtext';
import { Password } from 'primereact/password';
import { Dropdown, DropdownChangeEvent } from 'primereact/dropdown';
import { Message } from 'primereact/message';
import { Dialog } from 'primereact/dialog';
import { Language } from '../types';

interface LanguageOption {
  label: string;
  value: Language;
}

interface MessageState {
  type: 'success' | 'error' | '';
  text: string;
}

function Settings(): React.ReactElement {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { user, updateUser, logout } = useAuth();
  const { language, changeLanguage } = useLanguage();
  const [name, setName] = useState<string>(user?.name || '');
  const [email, setEmail] = useState<string>(user?.email || '');
  const [oldPassword, setOldPassword] = useState<string>('');
  const [newPassword, setNewPassword] = useState<string>('');
  const [confirmPassword, setConfirmPassword] = useState<string>('');
  const [loading, setLoading] = useState<boolean>(false);
  const [message, setMessage] = useState<MessageState>({ type: '', text: '' });
  const [deleteDialogVisible, setDeleteDialogVisible] = useState<boolean>(false);
  const [deletePassword, setDeletePassword] = useState<string>('');
  const [deleting, setDeleting] = useState<boolean>(false);

  const languages: LanguageOption[] = [
    { label: 'العربية', value: 'ar' },
    { label: 'English', value: 'en' }
  ];

  const handleUpdateProfile = async (): Promise<void> => {
    setLoading(true);
    try {
      const response = await userService.updateSettings({ name, email });
      if (response.data.success && user) {
        updateUser({ ...user, name, email });
        setMessage({ type: 'success', text: t('common.success') });
      } else {
        setMessage({ type: 'error', text: response.data.message || t('common.error') });
      }
    } catch (error) {
      setMessage({ type: 'error', text: apiErrorMessage(error) || t('common.error') });
    } finally {
      setLoading(false);
    }
  };

  const handleChangePassword = async (): Promise<void> => {
    if (newPassword !== confirmPassword) {
      setMessage({ type: 'error', text: t('auth.passwordsNoMatch') });
      return;
    }
    if (newPassword.length < 8) {
      setMessage({ type: 'error', text: t('auth.passwordMinLength') });
      return;
    }

    setLoading(true);
    try {
      const response = await userService.changePassword(oldPassword, newPassword);
      if (response.data.success) {
        setMessage({ type: 'success', text: t('common.success') });
        setOldPassword('');
        setNewPassword('');
        setConfirmPassword('');
      } else {
        setMessage({ type: 'error', text: response.data.message || t('common.error') });
      }
    } catch (error) {
      setMessage({ type: 'error', text: apiErrorMessage(error) || t('common.error') });
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteAccount = async (): Promise<void> => {
    setDeleting(true);
    try {
      const response = await userService.deleteAccount(deletePassword);
      if (response.data.success) {
        logout();
        navigate('/login');
      } else {
        setMessage({ type: 'error', text: response.data.message || t('common.error') });
        setDeleteDialogVisible(false);
      }
    } catch (error) {
      setMessage({ type: 'error', text: apiErrorMessage(error) || t('common.error') });
      setDeleteDialogVisible(false);
    } finally {
      setDeleting(false);
      setDeletePassword('');
    }
  };

  const handleLanguageChange = (e: DropdownChangeEvent): void => {
    changeLanguage(e.value as Language);
  };

  return (
    <div>
      <h1 className="text-2xl font-bold mb-4">{t('settings.title')}</h1>

      {message.text && (
        <Message severity={message.type || 'info'} text={message.text} className="w-full mb-4" />
      )}

      {/* Language Settings */}
      <Card className="mb-4">
        <h3 className="mt-0 mb-3">{t('settings.language')}</h3>
        <Dropdown
          value={language}
          options={languages}
          onChange={handleLanguageChange}
          className="w-full md:w-auto"
          style={{ minWidth: '200px' }}
        />
      </Card>

      {/* Account Settings */}
      <Card className="mb-4">
        <h3 className="mt-0 mb-3">{t('settings.account')}</h3>
        
        <div className="field mb-3">
          <label htmlFor="name" className="block mb-2 font-medium">
            {t('auth.name')}
          </label>
          <InputText
            id="name"
            value={name}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) => setName(e.target.value)}
            className="w-full"
          />
        </div>

        <div className="field mb-4">
          <label htmlFor="email" className="block mb-2 font-medium">
            {t('auth.email')}
          </label>
          <InputText
            id="email"
            type="email"
            value={email}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) => setEmail(e.target.value)}
            className="w-full"
          />
        </div>

        <Button
          label={t('common.save')}
          onClick={handleUpdateProfile}
          loading={loading}
        />
      </Card>

      {/* Change Password */}
      <Card className="mb-4">
        <h3 className="mt-0 mb-3">{t('settings.changePassword')}</h3>

        <div className="field mb-3">
          <label htmlFor="oldPassword" className="block mb-2 font-medium">
            Current Password
          </label>
          <Password
            id="oldPassword"
            value={oldPassword}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) => setOldPassword(e.target.value)}
            className="w-full"
            inputClassName="w-full"
            feedback={false}
            toggleMask
          />
        </div>

        <div className="field mb-3">
          <label htmlFor="newPassword" className="block mb-2 font-medium">
            New Password
          </label>
          <Password
            id="newPassword"
            value={newPassword}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) => setNewPassword(e.target.value)}
            className="w-full"
            inputClassName="w-full"
            toggleMask
          />
        </div>

        <div className="field mb-4">
          <label htmlFor="confirmPassword" className="block mb-2 font-medium">
            {t('auth.confirmPassword')}
          </label>
          <Password
            id="confirmPassword"
            value={confirmPassword}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) => setConfirmPassword(e.target.value)}
            className="w-full"
            inputClassName="w-full"
            feedback={false}
            toggleMask
          />
        </div>

        <Button
          label={t('settings.changePassword')}
          onClick={handleChangePassword}
          loading={loading}
          disabled={!oldPassword || !newPassword}
        />
      </Card>

      {/* Danger Zone */}
      <Card className="border-1 border-red-300">
        <h3 className="mt-0 mb-3 text-red-500">{t('settings.deleteAccount')}</h3>
        <p className="text-color-secondary mb-3">
          {t('settings.deleteAccountWarning')}
        </p>
        <Button
          label={t('settings.deleteAccount')}
          className="p-button-danger"
          icon="pi pi-trash"
          onClick={() => {
            setDeletePassword('');
            setDeleteDialogVisible(true);
          }}
        />
      </Card>

      {/* Delete Account Confirmation */}
      <Dialog
        header={t('settings.deleteAccount')}
        visible={deleteDialogVisible}
        onHide={() => setDeleteDialogVisible(false)}
        style={{ width: '90vw', maxWidth: '400px' }}
      >
        <p className="mt-0 mb-3">{t('settings.deleteAccountWarning')}</p>
        <div className="field mb-4">
          <label htmlFor="deletePassword" className="block mb-2 font-medium">
            {t('settings.deleteConfirmPassword')}
          </label>
          <Password
            id="deletePassword"
            value={deletePassword}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) => setDeletePassword(e.target.value)}
            className="w-full"
            inputClassName="w-full"
            feedback={false}
            toggleMask
          />
        </div>
        <div className="flex justify-content-end gap-2">
          <Button
            label={t('common.cancel')}
            className="p-button-text"
            onClick={() => setDeleteDialogVisible(false)}
          />
          <Button
            label={t('settings.deleteAccount')}
            className="p-button-danger"
            icon="pi pi-trash"
            onClick={handleDeleteAccount}
            disabled={!deletePassword}
            loading={deleting}
          />
        </div>
      </Dialog>
    </div>
  );
}

export default Settings;
